/**
 * Jobnachrichten zwischen API, Scheduler und Worker. Version 1 (23.10.2026).
 * Transport: SQS FIFO, MessageGroupId = tenantId, Deduplication-ID = jobId.
 * Siehe docs/JOBS_UND_DATEN.md.
 */
import { z } from 'zod';
import { EbayItemId, EbaySiteId, IsoDateTime, TenantId, Version } from './entities.js';

export const JobOrigin = z.enum(['schedule', 'user', 'system', 'migration']);

const base = {
  version: z.literal(1),
  /** Eindeutig je fachlichem Auftrag; dient als SQS-Deduplication-ID. */
  jobId: z.string().min(8).max(128),
  tenantId: TenantId,
  origin: JobOrigin,
  requestedAt: IsoDateTime,
  /** Wird bei Fortsetzungen und Wiederholungen hochgezählt. */
  attempt: z.number().int().nonnegative().default(0),
};

export const SyncReconcileMessage = z.object({
  ...base,
  type: z.literal('sync.reconcile'),
  payload: z.object({
    /** Lauf-ID; alle Seiten eines Laufs teilen sie. Items, die der Lauf nicht sieht, werden danach beendet. */
    runId: z.string().min(8).max(64),
    page: z.number().int().positive().default(1),
    /** Fenster wie im Altsystem: Endezeit −3/+60 Tage ab Laufstart. */
    endTimeFrom: IsoDateTime,
    endTimeTo: IsoDateTime,
    /** Optional, falls GetSellerList pro Site iterieren muss (Prüfung in Inkrement 1). */
    site: EbaySiteId.optional(),
    /**
     * 'user-change' (Opus-Anfrage 23.10.2026): Der Kunde hat beim Neuverbinden einen anderen
     * eBay-Nutzer autorisiert. Das Altsystem leerte dabei Artikeltabelle und Shopkategorien.
     * Neu: laufenden Lauf ablösen, alle Angebote des Kontos als beendet markieren, EMBED# auf
     * 'unknown', STORECATS leeren, dann vollständig neu einlesen. Fehlt = 'scheduled'.
     */
    /** 'user-change' = anderer eBay-Verkäufer verbunden (setzt articleId/EMBED-Pins zurück); 'token-renewed' = derselbe Verkäufer mit neuem Token, nicht destruktiv (24.10.2026). */
    reason: z.enum(['scheduled', 'user', 'import', 'user-change', 'token-renewed']).optional(),
  }),
});

export const SyncDeltaMessage = z.object({
  ...base,
  type: z.literal('sync.delta'),
  payload: z.object({
    /** Lauf-ID des Delta-Laufs; Teilfenster teilen sie. Optional bis v0.2. */
    runId: z.string().min(8).max(64).optional(),
    modTimeFrom: IsoDateTime,
    modTimeTo: IsoDateTime,
    /** Tiefe der Fensterhalbierung bei 3.000 Treffern. */
    split: z.number().int().min(0).max(12).default(0),
  }),
});

export const ItemDetailsMessage = z.object({
  ...base,
  type: z.literal('item.details'),
  payload: z.object({ ebayItemIds: z.array(EbayItemId).min(1).max(20) }),
});

export const StoreSyncMessage = z.object({
  ...base,
  type: z.literal('store.sync'),
  payload: z.object({}).default({}),
});

/** Internal read-master/purge continuation. Never authorizes an eBay request. */
export const SyncControlMessage = z.object({
  ...base,
  type: z.literal('sync.control'),
  origin: z.literal('system'),
  payload: z.object({ generation: IsoDateTime.nullable(), step: z.number().int().nonnegative().safe() }).strict(),
}).strict();
export type SyncControlMessage = z.infer<typeof SyncControlMessage>;

export const RulesEvaluateMessage = z.object({
  ...base,
  type: z.literal('rules.evaluate'),
  payload: z.object({
    /** Leer = alle Sites des Kontos nacheinander. */
    site: EbaySiteId.optional(),
    /** Leer = alle Galerien der Site. */
    galleryIds: z.array(z.string()).max(50).optional(),
    /** Leer = alle eingebetteten Ausgangsangebote. */
    sourceItemIds: z.array(EbayItemId).max(100).optional(),
    reason: z.enum(['items-changed', 'rules-changed', 'gallery-changed', 'user', 'reconcile-complete']),
  }),
});

/** Anlass eines Beschreibungsauftrags; Reconcile reicht ihn unverändert an Revise weiter (auch durch Retry/Refresh). */
export const DescriptionReason = z.enum(['selection-changed', 'rules-changed', 'gallery-changed', 'article-published', 'design-published', 'user', 'assigned', 'marker-detected', 'gallery-deleted', 'customer-ordered']);
export type DescriptionReason = z.infer<typeof DescriptionReason>;

export const DescriptionReconcileMessage = z.object({
  ...base,
  type: z.literal('description.reconcile'),
  payload: z.object({
    ebayItemIds: z.array(EbayItemId).max(100).optional(),
    articleId: z.string().optional(),
    designId: z.string().optional(),
    galleryId: z.string().optional(),
    /**
     * Anlass (Festlegung 25.10.2026, Demo 1): 'selection-changed' = automatische Auswahländerung durch Angebotsdaten
     * (unterliegt dem 48-h-Halt bei reiner Listenänderung); 'rules-changed' und 'gallery-changed' = vom Nutzer veranlasste
     * Regel-/Einstellungsänderung, umgeht nur diesen Halt, nicht das Budget; 'assigned' = Zuordnung eines eigenen Angebots zu
     * einem Artikel in der App (Aufnahme, erster Lauf nur lesen); 'marker-detected' = sync.delta fand in einem eigenen,
     * unzugeordneten Angebot den PAT-Galeriemarker einer eigenen Galerie (Aufnahme im Blockmodus, erster Lauf nur lesen);
     * 'gallery-deleted' = Galerie gelöscht, der Worker entfernt ihren Block aus den Beschreibungen; 'customer-ordered' =
     * ausdrücklicher Schreibauftrag des Kunden für genau dieses Angebot, einzige Autorität für vollständige
     * Artikelbeschreibungen (Flori, 25.10.2026); 'article-published'/'design-published' lösen seitdem keinen Vollschreibvorgang aus.
     */
    reason: DescriptionReason,
    /**
     * Veröffentlichte Artikelfassung, die der Kunde beauftragt hat (Opus-1/Flori, 25.10.2026). Pflicht bei 'customer-ordered'
     * (siehe descriptionOrderIsConsistent): Der Worker schreibt nur, wenn `ARTICLE#.publishedVersion` bei Ausführung noch
     * gleich ist, sonst endet der Auftrag terminal mit Grund `article-changed` und die App bittet um einen neuen Auftrag.
     * Damit ändert ein zwischenzeitliches Veröffentlichen nie indirekt eine Vollschreibung.
     */
    articleVersion: Version.optional(),
    /**
     * Ausdrücklicher Auftrag des Kunden, eine bei eBay fremd geänderte Beschreibung (`diverged`)
     * zu überschreiben (Flori, 24.10.2026). Nur zusammen mit genau diesem einen Eintrag in
     * `ebayItemIds`; `divergedHtmlHash` muss dem beim Vorabvergleich vorgefundenen HTML entsprechen,
     * sonst bricht der Worker ab (zwischenzeitlich erneut geändert). Outbox-Grund `user-change`.
     */
    overwrite: z.object({ ebayItemId: EbayItemId, divergedHtmlHash: z.string().regex(/^[a-f0-9]{64}$/) }).optional(),
  }),
});
/** Invariante zu `payload.overwrite` (kein Refine, damit `.strict()`/Unions auf dem Objekt weiter funktionieren): nur mit genau diesem einen Angebot in `ebayItemIds`. Erzeuger (packages/data) und Worker prüfen damit. */
/** Invariante zu 'customer-ordered' (kein Refine): genau ein Angebot und die beauftragte `articleVersion` müssen gesetzt sein. */
export function descriptionOrderIsConsistent(payload: { reason: string; ebayItemIds?: readonly string[] | undefined; articleId?: string | undefined; articleVersion?: number | undefined }): boolean {
  return payload.reason !== 'customer-ordered' || (payload.ebayItemIds?.length === 1 && typeof payload.articleId === 'string' && payload.articleId.length > 0 && typeof payload.articleVersion === 'number');
}
/**
 * Invariante für Vollschreiben in `description.revise` (Codex-1, 25.10.2026): bei 'customer-ordered' müssen `expected.articleId`
 * und `expected.articleVersion` gesetzt sein; der Worker prüft beide gegen ITEM/ARTICLE. Eine Umzuordnung des Angebots auf einen
 * anderen Artikel mit gleicher numerischer Version darf den alten Auftrag nie ausführen (terminal `article-changed`).
 */
/** Invariante zu 'gallery-deleted' (kein Refine): Reconcile und Revise müssen die gelöschte `galleryId` tragen. */
export function descriptionGalleryDeletionIsConsistent(payload: { reason?: string | undefined; galleryId?: string | undefined }): boolean {
  return payload.reason !== 'gallery-deleted' || (typeof payload.galleryId === 'string' && payload.galleryId.length > 0);
}
export const reviseGalleryDeletionIsConsistent = descriptionGalleryDeletionIsConsistent;
export function reviseFullWriteIsConsistent(payload: { reason?: string | undefined; expected: { articleId?: string | undefined; articleVersion?: number | undefined } }): boolean {
  return payload.reason !== 'customer-ordered' || (typeof payload.expected.articleId === 'string' && payload.expected.articleId.length > 0 && typeof payload.expected.articleVersion === 'number');
}
/** Invariante für `description.revise`: die Freigabe gilt nur für genau dieses Angebot. */
export function reviseOverwriteIsConsistent(payload: { ebayItemId: string; overwrite?: { ebayItemId: string } | undefined }): boolean {
  return !payload.overwrite || payload.overwrite.ebayItemId === payload.ebayItemId;
}
export function descriptionOverwriteIsConsistent(payload: { ebayItemIds?: readonly string[] | undefined; overwrite?: { ebayItemId: string } | undefined }): boolean {
  return !payload.overwrite || (payload.ebayItemIds?.length === 1 && payload.ebayItemIds[0] === payload.overwrite.ebayItemId);
}

export const DescriptionReviseMessage = z.object({
  ...base,
  type: z.literal('description.revise'),
  payload: z.object({
    ebayItemId: EbayItemId,
    mode: z.enum(['full', 'block']),
    /**
     * Anlass aus dem erzeugenden Reconcile (25.10.2026). Optional für alte Aufträge; fehlt er, darf der Worker **kein**
     * Vollschreiben ausführen. Vollschreiben verlangt `reason === 'customer-ordered'` und `origin === 'user'` (Flori:
     * ausdrücklicher Auftrag je Angebot); Galerieblöcke laufen mit den übrigen Anlässen.
     */
    reason: DescriptionReason.optional(),
    /**
     * Genau die gelöschte Galerie (25.10.2026, Codex-1): Pflicht bei reason 'gallery-deleted' (reviseGalleryDeletionIsConsistent);
     * der Worker reicht die beauftragte ID unverändert durch Child/Retry/Refresh und entfernt nur diesen Block, keine Ableitung
     * aus selectionHashes, JobID oder DOM.
     */
    galleryId: z.string().min(1).optional(),
    /** Erwartete Versionen; weicht der Datenstand ab, wird neu berechnet statt geschrieben. */
    expected: z.object({
      /** Artikel, an den das Angebot beim Erzeugen gebunden war; Pflicht bei 'customer-ordered' (reviseFullWriteIsConsistent). */
      articleId: z.string().min(1).optional(),
      articleVersion: z.number().int().nonnegative().optional(),
      designVersion: z.number().int().nonnegative().optional(),
      selectionHashes: z.record(z.string(), z.string()).optional(),
    }),
    /** Weitergabe der ausdrücklichen Kundenfreigabe aus `description.reconcile` (24.10.2026); Invariante: `payload.ebayItemId === overwrite.ebayItemId`, siehe reviseOverwriteIsConsistent. Der Worker prüft vor dem Schreiben erneut den Live-HTML-Hash. */
    overwrite: z.object({ ebayItemId: EbayItemId, divergedHtmlHash: z.string().regex(/^[a-f0-9]{64}$/) }).optional(),
  }),
});

export const TokenCheckMessage = z.object({
  ...base,
  type: z.literal('token.check'),
  payload: z.object({}).default({}),
});

/** Globaler Job ohne Mandant: eBay-Kategoriebaum einer Site laden (Trading GetCategories). FIFO-Gruppe 'global'. */
export const CategorySyncMessage = z.object({
  ...base,
  tenantId: z.literal('global'),
  type: z.literal('category.sync'),
  payload: z.object({
    site: EbaySiteId,
    /** Nur laden, wenn eBay eine neuere CategoryVersion meldet; leer = immer. */
    knownVersion: z.string().max(32).optional(),
  }),
});

export const TenantImportMessage = z.object({
  ...base,
  type: z.literal('tenant.import'),
  payload: z.object({
    /** S3-Schlüssel der Exportdatei im privaten Export-Bucket. */
    exportObjectKey: z.string().min(1).max(512),
    exportSha256: z.string().regex(/^[a-f0-9]{64}$/),
  }),
});

export const JobMessage = z.discriminatedUnion('type', [
  SyncReconcileMessage,
  SyncDeltaMessage,
  ItemDetailsMessage,
  StoreSyncMessage,
  SyncControlMessage,
  RulesEvaluateMessage,
  DescriptionReconcileMessage,
  DescriptionReviseMessage,
  TokenCheckMessage,
  TenantImportMessage,
]);
export type JobMessage = z.infer<typeof JobMessage>;

/** Mandantengebundene Jobs plus globale Jobs; der Worker-Dispatcher unterscheidet über tenantId === 'global'. */
export const AnyJobMessage = z.union([JobMessage, CategorySyncMessage]);
export type AnyJobMessage = z.infer<typeof AnyJobMessage>;

/**
 * Kontrollnachricht des regulären unresolved-Wegs (Entscheidung Claude 28.10.2026): weckt genau einen Klärungsfall
 * (DescriptionResolution). Läuft **nicht** über die Work-FIFO (deren Gruppe kann hinter dem ungelösten Original stehen),
 * sondern über eine eigene Control-FIFO mit DLQ, Gruppe = tenantId, Dedup = jobId; gleiche Tenant-EXELEASE. Keine Phase,
 * kein HTML, kein Budget, kein `ready`/`desired` im Payload: der Worker lädt sein Recht nur aus den dauerhaften Rows.
 * `attempt` ist Transportdiagnose, kein Recht.
 */
export const DescriptionResolveMessage = z.object({
  ...base,
  type: z.literal('description.resolve'),
  origin: z.literal('system'),
  payload: z.object({
    resolutionId: z.string().regex(/^[a-f0-9]{64}$/),
    wakeRevision: z.number().int().nonnegative(),
  }).strict(),
}).strict();
export type DescriptionResolveMessage = z.infer<typeof DescriptionResolveMessage>;
/** Eigene Kontrollunion, getrennt von JobMessage/AnyJobMessage; der Work-Dispatcher nimmt sie nicht an. */
export const ControlJobMessage = z.discriminatedUnion('type', [DescriptionResolveMessage]);
export type ControlJobMessage = z.infer<typeof ControlJobMessage>;
/** Deterministische Kontrolljob-ID: je Fall und Wake-Revision genau eine Zustellung (unter 128 Zeichen). */
export function descriptionResolveJobId(resolutionId: string, wakeRevision: number): string {
  return `description.resolve#${resolutionId}#${wakeRevision}`;
}

/** SQS-Attribute, die der Sender setzen muss. */
export function sqsFifoAttributes(message: { tenantId: string; jobId: string }): { MessageGroupId: string; MessageDeduplicationId: string } {
  return { MessageGroupId: message.tenantId, MessageDeduplicationId: message.jobId };
}

/** Deterministische Job-ID für zeitplangetriebene Aufträge, damit Doppelstarts dedupliziert werden. */
export function scheduledJobId(type: JobMessage['type'], tenantId: string, bucketIso: string): string {
  return `${type}#${tenantId}#${bucketIso}`;
}

/**
 * Deterministische Job-ID je Schritt eines Laufs (Reconcile-Seite oder Delta-Teilfenster).
 * Gleiche runId und gleicher Schritt ergeben dieselbe ID, damit eine doppelt eingereihte
 * Fortsetzung im SQS-Dedup-Fenster verworfen wird. step: Reconcile p<page> oder
 * s<site>-p<page>; Delta w<from>-<to>-d<split>.
 */
export function stepJobId(type: 'sync.reconcile' | 'sync.delta', tenantId: string, runId: string, step: string): string {
  if (!/^[A-Za-z0-9:_.-]{1,80}$/.test(step)) throw new Error('invalid step');
  return `${type}#${tenantId}#${runId}#${step}`;
}

export function reconcileStep(page: number, site?: number): string {
  return site === undefined ? `p${page}` : `s${site}-p${page}`;
}

export function deltaStep(modTimeFrom: string, modTimeTo: string, split: number): string {
  return `w${modTimeFrom.replace(/[^0-9]/g, '')}-${modTimeTo.replace(/[^0-9]/g, '')}-d${split}`;
}

export function parseJobMessage(raw: unknown): JobMessage {
  return JobMessage.parse(typeof raw === 'string' ? JSON.parse(raw) : raw);
}
